import React from 'react';
import { Certification as CertificationConst } from '@axiom/const';
import {
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Button,
  Grid,
  GridRow,
  GridColumn,
  Form,
  Dropdown,
  Input,
} from '@axiom/ui';
import type {
  Candidate,
  CandidateRedux,
  Certification,
} from '@axiom/validation';

import { DateUtil } from '../../utils/date-util';
import { CandidateCertificationsApi } from '../../api/candidate-certifications-api';
import { CertificationFormSchema } from '../../schema/certification-form-schema';

const {
  QUALIFICATION_AWARDS: { QUALIFICATION, YEAR_OF_CALL, STATE_EXAM },
} = CertificationConst;

const YEARS_BACK = 80;

const certificationYears = DateUtil.getYearOptionsByRange(
  new Date().getFullYear(),
  new Date().getFullYear() - YEARS_BACK
);

export interface CertificationFormProps {
  onClose: () => void;
  certification?: Certification | null;
  certificationType: 'Certifications & Awards' | 'Qualification';
  refreshData: () => Promise<void>;
  candidate: CandidateRedux | Candidate;
  name: string;
}

export const CertificationForm = ({
  certification,
  onClose,
  certificationType,
  refreshData,
  candidate,
  name,
}: CertificationFormProps) => {
  const qualificationOptions = [QUALIFICATION, STATE_EXAM, YEAR_OF_CALL].map(
    value => ({ value })
  );
  return (
    <Form
      name={name}
      schema={CertificationFormSchema}
      onSubmit={async (changedData, _, formData) => {
        await (certification?.id
          ? CandidateCertificationsApi.updateCertification(
              certification.id,
              changedData
            )
          : CandidateCertificationsApi.createCertification(
              formData.candidateId,
              {
                ...formData,
              }
            ));
        await refreshData();
      }}
      initialValues={{
        candidateId: candidate.id,
        institution: certification?.institution,
        year: certification?.year,
        award: certification?.award,
        stateExamScore: certification?.stateExamScore,
      }}
    >
      {({ fireSubmit, values }) => {
        return (
          <Modal name="CERTFORM">
            <ModalHeader
              name="CERTIFICATION_MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              {certification?.id ? 'Edit' : 'Add'} {certificationType}
            </ModalHeader>
            <ModalSection disableScrolling>
              <Grid>
                {certificationType === 'Qualification' ? (
                  <>
                    <GridRow
                      gutterBottom="16px"
                      name="Qualification_Form_Element"
                    >
                      <GridColumn>
                        <Input name="institution" label="Institution" />
                      </GridColumn>
                    </GridRow>
                    <GridRow gutterBottom="16px">
                      <GridColumn width={6}>
                        <Dropdown
                          label="Degree"
                          displayKey="value"
                          name="award"
                          options={qualificationOptions}
                          valueKey="value"
                        />
                      </GridColumn>
                      <GridColumn width={6}>
                        <Dropdown
                          options={certificationYears}
                          label="Year"
                          displayKey="label"
                          valueKey="value"
                          name="year"
                        />
                      </GridColumn>
                    </GridRow>
                    {values.award === STATE_EXAM && (
                      <GridRow>
                        <GridColumn width={12}>
                          <Input name="stateExamScore" label="Score" />
                        </GridColumn>
                      </GridRow>
                    )}
                  </>
                ) : (
                  <>
                    <GridRow
                      gutterBottom="16px"
                      name="Certification_Form_Element"
                    >
                      <GridColumn width={6}>
                        <Input
                          name="award"
                          label="Certification / Award Name"
                        />
                      </GridColumn>
                      <GridColumn width={6}>
                        <Input
                          name="institution"
                          label="Awarding Organization"
                        />
                      </GridColumn>
                    </GridRow>
                    <GridRow gutterBottom="16px">
                      <GridColumn width={12}>
                        <Dropdown
                          label="Year"
                          displayKey="label"
                          name="year"
                          options={certificationYears}
                          valueKey="value"
                        />
                      </GridColumn>
                    </GridRow>
                  </>
                )}
              </Grid>
            </ModalSection>
            <ModalFooter>
              <Button
                name="CANCELCERTIFICATIONBUTTON"
                onClick={() => {
                  onClose();
                }}
                variation="outline"
              >
                Cancel
              </Button>
              <Button name="SAVECERTIFICATIONBUTTON" onClick={fireSubmit}>
                Save
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
